// frontend/src/services/api.js
import axios from 'axios';
import { supabase } from '../lib/supabaseClient';

// 環境変数からバックエンドのベースURLを取得
let API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Cloud Run の URL で http になっている場合は https に強制変換
if (API_URL.includes('run.app') && API_URL.startsWith('http://')) {
  API_URL = API_URL.replace('http://', 'https://');
}

if (process.env.NODE_ENV !== 'production') {
  console.log('Current API_URL:', API_URL);
}

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

const getStripeBaseUrl = () => {
  if (API_URL.endsWith('/api/')) {
    return API_URL.slice(0, -5);
  }
  if (API_URL.endsWith('/api')) {
    return API_URL.slice(0, -4);
  }
  return API_URL;
};

// トークンを取得する関数（キャッシュ付き）
const getAuthToken = async (forceRefresh = false) => {
  if (!supabase) return null;

  if (forceRefresh) {
    try {
      await supabase.auth.refreshSession();
    } catch {
      // ignore
    }
  }

  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || null;
};

// インターセプター
api.interceptors.request.use(
  async (config) => {
    try {
      const token = await getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    } catch (error) {
      // セキュリティ: エラーの詳細をログに出力しない
      return Promise.reject(error);
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

const MAX_RETRIES = 2;
const INITIAL_BACKOFF = 1000; // 1秒

// Simple Cache Implementation
const apiCache = new Map();

/**
 * Wraps a fetcher function with caching logic.
 * @param {string} key - Unique cache key
 * @param {Function} fetcher - Function that returns a promise
 * @param {number} ttl - Time to live in milliseconds
 */
const withCache = async (key, fetcher, ttl) => {
  const cached = apiCache.get(key);
  const now = Date.now();
  if (cached && (now - cached.timestamp < ttl)) {
    return cached.data;
  }
  const data = await fetcher();
  apiCache.set(key, { data, timestamp: now });
  return data;
};

export const clearApiCache = () => {
  apiCache.clear();
};

// Cache TTL constants
const TTL_MASTER_DATA = 60 * 60 * 1000; // 60 minutes
const TTL_USER_DATA = 5 * 60 * 1000;    // 5 minutes
const TTL_CAPACITY_DATA = 10 * 60 * 1000; // 10 minutes

// リトライロジック（バックオフ戦略付き）
const retryRequest = async (requestFunc, retries = 0, backoff = INITIAL_BACKOFF) => {
  try {
    return await requestFunc();
  } catch (error) {
    if (error.response?.status === 401 && retries < MAX_RETRIES) {
      // セキュリティ: リトライ情報をログに出力しない

      // バックオフ待機
      await new Promise(resolve => setTimeout(resolve, backoff));

      try {
        // トークンを強制更新
        await getAuthToken(true);
        // 次のリトライではバックオフ時間を2倍に
        return retryRequest(requestFunc, retries + 1, backoff * 2);
      } catch (tokenError) {
        // トークン更新に失敗した場合は再試行
        return retryRequest(requestFunc, retries + 1, backoff * 2);
      }
    }
    throw error;
  }
};

export const createCheckoutSession = async (payload) => {
  return retryRequest(async () => {
    const response = await api.post(`${getStripeBaseUrl()}/stripe/checkout-session`, payload);
    return response.data;
  });
};

export const getMyLatestPayment = async () => {
  return retryRequest(async () => {
    const response = await api.get(`${getStripeBaseUrl()}/stripe/payments/me?latest=true`);
    return response.data;
  });
};

export const getPaymentStatus = async () => {
  return retryRequest(async () => {
    const response = await api.get(`${getStripeBaseUrl()}/stripe/payment-status`);
    return response.data;
  });
};

export const createPaymentAllocation = async (payload) => {
  return retryRequest(async () => {
    const response = await api.post(`${getStripeBaseUrl()}/stripe/allocations`, payload);
    return response.data;
  });
};

export const getEligibleUsers = async () => {
  return retryRequest(async () => {
    const response = await api.get(`${getStripeBaseUrl()}/stripe/eligible-users`);
    return response.data;
  });
};

export const getAdminPaymentStatuses = async () => {
  return retryRequest(async () => {
    const response = await api.get(`${getStripeBaseUrl()}/stripe/admin/payment-statuses`);
    return response.data;
  });
};

/**
 * ユーザー情報を取得
 */
export const getUserInfo = async () => {
  return withCache('user_info_me', () => retryRequest(async () => {
    try {
      const response = await api.get('/users/me');
      return response.data;
    } catch (error) {
      throw error;
    }
  }), TTL_USER_DATA);
};

/**
 * キャッシュを更新しながらユーザー情報を同期
 */
export const syncUserInfo = async () => {
  const data = await retryRequest(async () => {
    const response = await api.post('/users/me/sync-email');
    return response.data;
  });
  apiCache.set('user_info_me', { data, timestamp: Date.now() });
  return data;
};

export const upsertUserProfile = async (payload) => {
  return retryRequest(async () => {
    const response = await api.post('/users/', payload);
    return response.data;
  });
};

export const updateUserAdminStatus = async (userId, isAdmin) => {
  return retryRequest(async () => {
    const response = await api.patch(`/users/${userId}/admin-update`, {
      flag_name: 'is_admin',
      flag_value: isAdmin
    });
    return response.data;
  });
};

/**
 * 病院一覧
 */
export const getInstitutions = async () => {
  return withCache('institutions', () => retryRequest(async () => {
    const response = await api.get('/institutions/institutions');
    return response.data;
  }), TTL_MASTER_DATA);
};

export const createInstitution = async (payload) => {
  return retryRequest(async () => {
    const response = await api.post('/institutions/institutions', payload);
    return response.data;
  });
};

export const updateInstitution = async (institutionId, payload) => {
  return retryRequest(async () => {
    const response = await api.put(`/institutions/institutions/${institutionId}`, payload);
    return response.data;
  });
};

export const deleteInstitution = async (institutionId) => {
  return retryRequest(async () => {
    const response = await api.delete(`/institutions/institutions/${institutionId}`);
    return response.data;
  });
};

export const getDepartments = async () => {
  return withCache('departments', () => retryRequest(async () => {
    const response = await api.get('/departments/departments');
    return response.data;
  }), TTL_MASTER_DATA);
};

export const createDepartment = async (payload) => {
  return retryRequest(async () => {
    const response = await api.post('/departments/departments', payload);
    return response.data;
  });
};

export const updateDepartment = async (departmentId, payload) => {
  return retryRequest(async () => {
    const response = await api.put(`/departments/departments/${departmentId}`, payload);
    return response.data;
  });
};

export const deleteDepartment = async (departmentId) => {
  return retryRequest(async () => {
    const response = await api.delete(`/departments/departments/${departmentId}`);
    return response.data;
  });
};

/**
 * 部門期間capacity (病院ID, 科ID, termIDなど)
 */
export const getDepartmentTermCapacities = async () => {
  return withCache('department_term_capacities', () => retryRequest(async () => {
    const response = await api.get('/department-term-capacities');
    return response.data;
  }), TTL_CAPACITY_DATA);
};



export const createDepartmentTermCapacity = async (payload) => {
  return retryRequest(async () => {
    const response = await api.post('/department-term-capacities', payload);
    return response.data;
  });
};

export const updateDepartmentTermCapacity = async (capacityId, payload) => {
  return retryRequest(async () => {
    const response = await api.put(`/department-term-capacities/${capacityId}`, payload);
    return response.data;
  });
};

export const deleteDepartmentTermCapacity = async (capacityId) => {
  return retryRequest(async () => {
    const response = await api.delete(`/department-term-capacities/${capacityId}`);
    return response.data;
  });
};

export const getTerms = async () => {
  return withCache('terms', () => retryRequest(async () => {
    const response = await api.get('/terms/terms');
    return response.data;
  }), TTL_MASTER_DATA);
};

export const getTermCapacities = async (skip = 0, limit = 1000) => {
  return withCache(`term_capacities_${skip}_${limit}`, () => retryRequest(async () => {
    const response = await api.get(`/term-capacities/term-capacities?skip=${skip}&limit=${limit}`);
    return response.data;
  }), TTL_CAPACITY_DATA);
};

export const createTermCapacity = async (payload) => {
  return retryRequest(async () => {
    const response = await api.post('/term-capacities/term-capacities', payload);
    return response.data;
  });
};

export const updateTermCapacity = async (capacityId, payload) => {
  return retryRequest(async () => {
    const response = await api.put(`/term-capacities/term-capacities/${capacityId}`, payload);
    return response.data;
  });
};

export const deleteTermCapacity = async (capacityId) => {
  return retryRequest(async () => {
    const response = await api.delete(`/term-capacities/term-capacities/${capacityId}`);
    return response.data;
  });
};

export const sendResetEmail = async (email) => {
  return retryRequest(async () => {
    try {
      const response = await api.post('/forgot-password', { email });
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  });
};

export const resetPassword = async (email, tempPassword, newPassword) => {
  return retryRequest(async () => {
    try {
      const response = await api.post('/reset-password', { email, tempPassword, newPassword });
      return response.data;
    } catch (error) {
      throw error.response.data;
    }
  });
};

export const getUserHospitalExchangePreferences = async () => {
  return retryRequest(async () => {
    try {
      const response = await api.get('/hospital-exchange-requests/');
      // レスポンス内の各preference に source_department_term_capacity_id が含まれている前提
      const mappedData = response.data.map((requestItem) => {
        const mappedPreferences = requestItem.preferences.map((pref) => {
          const rawComplements = pref.complement_preferences || [];
          let mappedComplements =
            rawComplements.length > 0
              ? rawComplements.map((comp) => ({
                term: comp.term,
                preference_order: comp.preference_order,
                accommodation: comp.accommodation,
                department_id: comp.department_id,
                institution_id: comp.institution_id,
                display_name: comp.display_name,
              }))
              : [];
          // 補完候補が3件に満たない場合、nullでパディング
          while (mappedComplements.length < 3) {
            mappedComplements.push(null);
          }
          return {
            sourceTerm: pref.source_term,
            targetTerm: pref.target_term,
            accommodation: pref.accommodation,
            candidate: {
              department_id: pref.department_id,
              institution_id: pref.institution_id,
              display_name: pref.display_name,
              term_id: pref.term_id,
            },
            // ここを追加
            sourceDepartmentTermCapacityId: pref.source_department_term_capacity_id,
            complementCandidates: mappedComplements,
          };
        });
        return {
          ...requestItem,
          preferences: mappedPreferences,
        };
      });
      return mappedData;
    } catch (error) {
      console.error(
        '病院交換希望の取得に失敗しました:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const createOrUpdateHospitalExchangeRequest = async (currentAssignmentId, preferences) => {
  const transformedPreferences = preferences.map((pref) => {
    let complementPreferences = [];
    if (pref.sourceTerm !== pref.targetTerm) {
      // 移動元と移動先が異なる場合、補完候補が3件全て入力されているかチェック
      if (
        !pref.complementCandidates ||
        pref.complementCandidates.length !== 3 ||
        pref.complementCandidates.some((comp) => comp === null)
      ) {
        throw new Error(`交換希望${pref.sourceTerm} の補完候補は必ず3件入力してください。`);
      }
      complementPreferences = pref.complementCandidates.map((comp, index) => ({
        term: Number(pref.sourceTerm),
        preference_order: index + 1,
        accommodation: Boolean(pref.accommodation),
        candidate: comp,
      }));
    }
    // 移動元と移動先が同一の場合は、補完候補は空配列として送信
    return {
      source_term: Number(pref.sourceTerm),
      target_term: Number(pref.targetTerm),
      accommodation: Boolean(pref.accommodation),
      candidate: pref.candidate,
      complement_preferences: complementPreferences,
    };
  });

  const payload = {
    current_assignment_id: currentAssignmentId,
    preferences: transformedPreferences,
  };

  const response = await api.post('/hospital-exchange-requests/', payload);
  return response.data;
};

// --- 地域実習交換（空き枠申請）---
export const getRuralExchangeVacancies = async () => {
  return retryRequest(async () => {
    const response = await api.get('/rural-exchange/vacancies');
    return response.data;
  });
};

export const getMyRuralExchangeRequest = async () => {
  return retryRequest(async () => {
    try {
      const response = await api.get('/rural-exchange/requests/me');
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return null;
      }
      throw error;
    }
  });
};

export const submitRuralExchangeRequest = async (details) => {
  return retryRequest(async () => {
    const payload = { details };
    const response = await api.post('/rural-exchange/requests', payload);
    return response.data;
  });
};

export const fetchPreferenceStage = async (stage) => {
  return retryRequest(async () => {
    try {
      const response = await api.get(`/preferences/${stage}`);
      return response.data;
    } catch (error) {
      console.error(
        `Failed to fetch unified preferences for stage ${stage}:`,
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const savePreferenceStage = async (stage, payload) => {
  return retryRequest(async () => {
    try {
      const response = await api.post(`/preferences/${stage}`, payload);
      return response.data;
    } catch (error) {
      console.error(
        `Failed to save unified preferences for stage ${stage}:`,
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const getUserFirstPreferences = async (userId) => {
  return retryRequest(async () => {
    try {
      const response = await api.get(`/first_preferences/first/${userId}`);
      return response.data;
    } catch (error) {
      console.error(
        'Failed to get first preferences:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const getResults = async (assignment_stage = 'final') => {
  return retryRequest(async () => {
    try {
      const response = await api.get(`/assignments/results?assignment_stage=${assignment_stage}`);
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return [];
      } else {
        console.error(
          '結果の取得に失敗しました:',
          error.response ? error.response.data : error.message
        );
        throw error;
      }
    }
  });
};

export const getSecondChoiceData = async () => {
  return retryRequest(async () => {
    try {
      const response = await api.get('/assignments/second-choice-results');
      return response.data;
    } catch (error) {
      console.error(
        'Failed to fetch second choice data:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const saveSecondPreferences = async (preferences) => {
  return retryRequest(async () => {
    try {
      const response = await api.post('/second-preferences', preferences);
      return response.data;
    } catch (error) {
      console.error(
        'Failed to save second preferences:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const postTimeChangeRequests = async (requests) => {
  try {
    const response = await api.post('/time-change-requests', { requests });
    return response.data;
  } catch (error) {
    console.error('Error posting time change requests:', error);
    throw error;
  }
};

export const getTimeChangeRequests = async () => {
  try {
    const response = await api.get('/time-change-requests');
    return response.data;
  } catch (error) {
    console.error('Error getting time change requests:', error);
    throw error;
  }
};

export const getUserExchangeRequests = async (userId) => {
  return retryRequest(async () => {
    try {
      const response = await api.get(`/exchange-requests/${userId}`);
      return response.data;
    } catch (error) {
      console.error(
        'Failed to get user exchange requests:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const createExchangeRequest = async (exchangeRequest) => {
  return retryRequest(async () => {
    try {
      const response = await api.post('/exchange-requests', exchangeRequest);
      return response.data;
    } catch (error) {
      console.error(
        'Failed to create exchange request:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const updateExchangeRequest = async (requestId, updateData) => {
  return retryRequest(async () => {
    try {
      const response = await api.put(`/exchange-requests/${requestId}`, updateData);
      return response.data;
    } catch (error) {
      console.error(
        'Failed to update exchange request:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const getAllExchangeRequests = async (skip = 0, limit = 100) => {
  return retryRequest(async () => {
    try {
      const response = await api.get(`/exchange-requests?skip=${skip}&limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error(
        'Failed to get all exchange requests:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const getExchangeRequest = async (requestId) => {
  return retryRequest(async () => {
    try {
      const response = await api.get(`/exchange-requests/${requestId}`);
      return response.data;
    } catch (error) {
      console.error(
        'Failed to get exchange request:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const deleteExchangeRequest = async (requestId) => {
  return retryRequest(async () => {
    try {
      const response = await api.delete(`/exchange-requests/${requestId}`);
      return response.data;
    } catch (error) {
      console.error(
        'Failed to delete exchange request:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const getUserRuralPreferences = async (userId) => {
  return retryRequest(async () => {
    try {
      const response = await api.get(`/rural_preferences/rural/${userId}`);
      return response.data;
    } catch (error) {
      console.error(
        'Failed to get rural preferences:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const updateUserRuralPreferences = async (userId, payload) => {
  return retryRequest(async () => {
    try {
      const response = await api.post(`/rural_preferences/rural/${userId}`, payload);
      return response.data;
    } catch (error) {
      console.error(
        'Failed to update rural preferences:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const updateUserFirstPreferences = async (userId, preferences) => {
  return retryRequest(async () => {
    try {
      const response = await api.post(`/first_preferences/first/${userId}`, preferences);
      return response.data;
    } catch (error) {
      console.error(
        'Failed to update first preferences:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const getAllUsers = async (skip = 0, limit = 100) => {
  // Admin data, cache for 5 minutes
  return withCache(`all_users_${skip}_${limit}`, () => retryRequest(async () => {
    const response = await api.get(`/users/?skip=${skip}&limit=${limit}`);
    return response.data;
  }), TTL_USER_DATA);
};

export const getUserHospitalExchangeRequests = async () => {
  return retryRequest(async () => {
    try {
      const response = await api.get('/hospital-exchange-requests/');
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return { requests: [] };
      } else {
        console.error(
          'ユーザーの病院交換リクエストの取得に失敗しました:',
          error.response ? error.response.data : error.message
        );
        throw error;
      }
    }
  });
};

export const createOrUpdateHospitalExchangeRequests = async (requests) => {
  return retryRequest(async () => {
    try {
      const response = await api.post('/hospital-exchange-requests/', requests);
      return response.data;
    } catch (error) {
      console.error(
        '病院交換リクエストの作成または更新に失敗しました:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const getApplicationStatus = async () => {
  return retryRequest(async () => {
    try {
      const response = await api.get('/application-status');
      return response.data;
    } catch (error) {
      console.error(
        '応募状況の取得に失敗しました:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export default api;

export const updateUserFlag = async (userId, flagName, flagValue) => {
  try {
    const response = await api.patch(`/users/${userId}/flag`, {
      flag_name: flagName,
      flag_value: flagValue,
    });
    return response.data;
  } catch (error) {
    console.error(
      'ユーザーフラグの更新に失敗しました:',
      error.response ? error.response.data : error.message
    );
    throw error;
  }
};

export const runAlgorithm = async (algorithmType) => {
  return retryRequest(async () => {
    try {
      const response = await api.post(`/assignments/run-algorithm/${algorithmType}`);
      return response.data;
    } catch (error) {
      console.error(
        `Failed to run ${algorithmType} algorithm:`,
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const getCurrentAssignments = async () => {
  return withCache('current_assignments', () => retryRequest(async () => {
    try {
      const response = await api.get('/assignments/current-assignments');
      return response.data;
    } catch (error) {
      console.error(
        '現在の配属情報の取得に失敗しました:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  }), TTL_USER_DATA);
};

export const getRuralDepartmentTermCapacities = async () => {
  return withCache('rural_department_term_capacities', () => retryRequest(async () => {
    try {
      const response = await api.get('/department-term-capacities/rural');
      return response.data;
    } catch (error) {
      console.error(
        '地域医療の定員情報の取得に失敗しました:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  }), TTL_MASTER_DATA);
};

export const getAccommodationPreferences = async () => {
  return retryRequest(async () => {
    try {
      const response = await api.get('/accommodation_preferences');
      return response.data;
    } catch (error) {
      console.error('宿泊希望の取得に失敗しました:', error);
      throw error;
    }
  });
};

export const createOrUpdateAccommodationPreference = async (assignmentId, accommodation) => {
  return retryRequest(async () => {
    try {
      const response = await api.put(`/accommodation_preferences/${assignmentId}`, { accommodation });
      return response.data;
    } catch (error) {
      console.error('宿泊希望の更新に失敗しました:', error);
      throw error;
    }
  });
};

export const getFirstResults = async () => {
  return retryRequest(async () => {
    try {
      const response = await api.get('/assignments/first-results');
      return response.data;
    } catch (error) {
      console.error(
        '1次抽選結果の取得に失敗しました:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const getSecondResults = async () => {
  return retryRequest(async () => {
    try {
      const response = await api.get('/assignments/second-results');
      return response.data;
    } catch (error) {
      console.error(
        '二次抽選結果の取得に失敗しました:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const getVotingResults = async () => {
  return retryRequest(async () => {
    try {
      const response = await api.get('/second-preferences/voting-results');
      return response.data;
    } catch (error) {
      console.error('投票結果の取得に失敗しました:', error);
      throw error;
    }
  });
};

export const registerSecondStageAssignments = async (lotteryModelId) => {
  return retryRequest(async () => {
    try {
      const response = await api.post(`/assignments/register-second-stage/${lotteryModelId}`);
      return response.data;
    } catch (error) {
      console.error(
        '二次抽選結果の登録に失敗しました:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const getLotteryModels = async () => {
  return withCache('lottery_models', () => retryRequest(async () => {
    try {
      const response = await api.get('/lottery-models/lottery-models');
      return response.data;
    } catch (error) {
      console.error(
        '抽選モデルの取得に失敗しました:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  }), TTL_MASTER_DATA);
};

export const getApplicationStatusDepartments = async () => {
  try {
    const response = await api.get('/application-status/departments');
    return response.data;
  } catch (error) {
    console.error('部科レベル応募状況の取得に失敗', error);
    throw error;
  }
};

export const getApplicationStatusTotals = async () => {
  try {
    const response = await api.get('/application-status/totals');
    return response.data;
  } catch (error) {
    console.error('合計行応募状況の取得に失敗', error);
    throw error;
  }
};

export const getAllUserAssignments = async () => {
  return retryRequest(async () => {
    try {
      const response = await api.get('/assignments/all-assignments');
      return response.data;
    } catch (error) {
      console.error(
        '全ユーザーの配属情報の取得に失敗しました:',
        error.response ? error.response.data : error.message
      );
    }
  });
};

export const getFinalAssignmentResults = async (stage) => {
  return retryRequest(async () => {
    try {
      const params = stage ? { assignment_stage: stage } : {};
      const response = await api.get('/assignments/final-results', { params });
      return response.data;
    } catch (error) {
      console.error(
        '最終配属結果の取得に失敗しました:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const getAvailableCombinations = async () => {
  return retryRequest(async () => {
    try {
      const response = await api.get('/assignments/available-combinations');
      return response.data;
    } catch (error) {
      console.error(
        '利用可能な組み合わせの取得に失敗しました:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const getExchangeStatus = async () => {
  return withCache('exchange_status', () => retryRequest(async () => {
    try {
      const response = await api.get('/exchange-status');
      return response.data;
    } catch (error) {
      console.error(
        '交換申請状況の取得に失敗しました:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  }), TTL_USER_DATA);
};

export const getRuralExchangeStatus = async () => {
  return withCache('rural_exchange_status', () => retryRequest(async () => {
    try {
      const response = await api.get('/rural-exchange/status');
      return response.data;
    } catch (error) {
      console.error(
        '地域実習交換申請状況の取得に失敗しました:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  }), TTL_USER_DATA);
};

export const runRuralExchange = async () => {
  return retryRequest(async () => {
    const response = await api.post('/rural-exchange/execute');
    return response.data;
  });
};

export const getFinalOnlyResults = async () => {
  return retryRequest(async () => {
    try {
      const response = await api.get('/assignments/final-only');
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return [];
      } else {
        console.error(
          '最終結果の取得に失敗しました:',
          error.response ? error.response.data : error.message
        );
        throw error;
      }
    }
  });
};

export const createHospitalReport = async (reportData) => {
  return retryRequest(async () => {
    try {
      const response = await api.post('/hospital-reports', reportData);
      return response.data;
    } catch (error) {
      console.error(
        '病院レポートの作成に失敗',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const updateHospitalReport = async (reportId, reportData) => {
  return retryRequest(async () => {
    try {
      const response = await api.put(`/hospital-reports/${reportId}`, reportData);
      return response.data;
    } catch (error) {
      console.error(
        '病院レポートの更新に失敗',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const getHospitalReportPDF = async (reportId) => {
  return retryRequest(async () => {
    try {
      // /hospital-reports/:reportId/pdf をGETし、pdfバイナリを受け取る
      const response = await api.get(`/hospital-reports/${reportId}/pdf`, {
        responseType: 'blob', // PDFバイナリをblobで受け取る
      });
      return response.data; // Blobが返る
    } catch (error) {
      console.error(
        'PDF取得に失敗',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

/**
 * 全件HospitalReport (ユーザ絞り込みなし)
 */
export const getHospitalReports = async () => {
  return retryRequest(async () => {
    try {
      // GET /hospital-reports
      const response = await api.get('/hospital-reports');
      return response.data; // [{...}, {...}] の形式を想定
    } catch (error) {
      console.error(
        '病院レポート一覧の取得に失敗',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

/**
 * departmentTermCapacityIdベースでCreate/Update
 */
export const createOrUpdateHospitalReport = async (reportData) => {
  return retryRequest(async () => {
    try {
      /**
       * バックエンドでは /hospital-reports/create-or-update (POST)
       * のようなエンドポイントを用意し、
       * departmentTermCapacityId をキーに検索→更新 or 新規作成する実装を行う
       */
      const response = await api.post('/hospital-reports/create-or-update', reportData);
      return response.data;
    } catch (error) {
      console.error(
        '病院レポートの作成または更新に失敗',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

/**
 * 病院・科ごとの検索一覧
 * (ユーザ絞り込みなし)
 * e.g. GET /hospital-reports?hospital_id=xxx ...
 */
export const getHospitalReportList = async (params = {}) => {
  return retryRequest(async () => {
    const queryParams = new URLSearchParams();
    if (params.hospital_id) queryParams.append('hospital_id', params.hospital_id);
    if (params.department_id) queryParams.append('department_id', params.department_id);
    if (params.term) queryParams.append('term', params.term);
    if (params.cohort_id) queryParams.append('cohort_id', params.cohort_id);
    if (params.typical_start_time) queryParams.append('typical_start_time', params.typical_start_time);
    if (params.typical_start_time_operator) queryParams.append('typical_start_time_operator', params.typical_start_time_operator);
    if (params.typical_end_time) queryParams.append('typical_end_time', params.typical_end_time);
    if (params.typical_end_time_operator) queryParams.append('typical_end_time_operator', params.typical_end_time_operator);
    if (params.late_days_per_week !== undefined && params.late_days_per_week !== null && params.late_days_per_week !== '') {
      queryParams.append('late_days_per_week', params.late_days_per_week);
    }
    if (params.late_days_per_week_operator) queryParams.append('late_days_per_week_operator', params.late_days_per_week_operator);
    if (params.free_study_time) queryParams.append('free_study_time', params.free_study_time);
    if (params.has_night_duty !== undefined && params.has_night_duty !== null && params.has_night_duty !== '') {
      queryParams.append('has_night_duty', params.has_night_duty);
    }
    if (params.busyness) queryParams.append('busyness', params.busyness);
    if (params.hospital_tour_option) queryParams.append('hospital_tour_option', params.hospital_tour_option);
    if (params.car_requirement) queryParams.append('car_requirement', params.car_requirement);
    if (params.assignment_burden) queryParams.append('assignment_burden', params.assignment_burden);
    if (params.self_study_availability) queryParams.append('self_study_availability', params.self_study_availability);
    if (params.hands_on_opportunity) queryParams.append('hands_on_opportunity', params.hands_on_opportunity);
    if (params.lodging_convenience) queryParams.append('lodging_convenience', params.lodging_convenience);
    if (params.teaching_quality) queryParams.append('teaching_quality', params.teaching_quality);
    if (params.question_approachability) queryParams.append('question_approachability', params.question_approachability);
    if (params.department_atmosphere) queryParams.append('department_atmosphere', params.department_atmosphere);
    if (params.doctor_personality) queryParams.append('doctor_personality', params.doctor_personality);
    if (params.breakfast) queryParams.append('breakfast', params.breakfast);
    if (params.lunch) queryParams.append('lunch', params.lunch);
    if (params.dinner) queryParams.append('dinner', params.dinner);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_order) queryParams.append('sort_order', params.sort_order);

    const queryStr = queryParams.toString() ? `?${queryParams.toString()}` : '';

    // Cache key based on query string. TTL 10 mins.
    return withCache(`hospital_report_list_${queryStr}`, async () => {
      // 全ユーザーの検索 GET /hospital-reports
      const response = await api.get(`/hospital-reports${queryStr}`);
      return response.data;
    }, 10 * 60 * 1000);
  });
};

export const getMissingHospitalReportPlacements = async () => {
  return retryRequest(async () => {
    try {
      const response = await api.get('/hospital-reports/missing');
      return response.data;
    } catch (error) {
      console.error(
        '申し送り未登録の配属の一覧の取得に失敗しました:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};


export const getHospitalReportById = async (reportId) => {
  try {
    const response = await api.get(`/hospital-reports/${reportId}`);
    return response.data;  // これで department_name, hospital_name, term なども含まれる
  } catch (error) {
    console.error('病院レポートの取得に失敗しました:', error);
    throw error;
  }
};

/**
 * 指定されたIDの病院レポートを削除する
 * @param {number} reportId - 削除するレポートのID
 * @param {object} verificationData - 削除検証用データ
 * @param {string} verificationData.verification_token - 確認コード
 * @param {string} verificationData.hospital_name - 入力された病院名
 * @param {string} verificationData.department_name - 入力された科名
 * @param {string} verificationData.user_name - 入力されたユーザー名
 * @returns {Promise<object>} - 成功時はバックエンドからのレスポンスデータ、失敗時はエラーオブジェクト
 */
export const deleteHospitalReport = async (reportId, verificationData = {}) => {
  // retryRequest を使うことで、認証トークンの問題などで一時的に失敗した場合にリトライを試みます
  return retryRequest(async () => {
    try {
      // DELETEリクエストを /hospital-reports/{reportId} エンドポイントに送信
      // 検証データをリクエストボディとして送信
      const response = await api.delete(`/hospital-reports/${reportId}`, {
        data: verificationData
      });
      // 成功した場合、バックエンドからのレスポンスデータを返す
      return response.data;
    } catch (error) {
      // エラーが発生した場合はログ出力
      console.error(
        `病院レポート(ID: ${reportId})の削除に失敗`,
        error.response ? error.response.data : error.message
      );
      // エラー情報を呼び出し元に伝えるために、エラーを再スローする
      throw error.response?.data || { message: 'レポートの削除中にエラーが発生しました。' };
    }
  });
};

/**
 * 指定された DepartmentTermCapacity ID と現在のユーザーに紐づく病院レポートを取得する
 * @param {number} capacityId - DepartmentTermCapacity の ID
 * @returns {Promise<object|null>} - レポートデータ、または存在しない場合は null
 */
export const getHospitalReportByCapacityId = async (capacityId, params = {}) => {
  return retryRequest(async () => {
    try {
      const queryParams = new URLSearchParams();
      if (params.cohort_id) {
        queryParams.append('cohort_id', params.cohort_id);
      }
      const queryStr = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await api.get(`/hospital-reports/by-capacity/${capacityId}${queryStr}`);
      // バックエンドにレポートがない場合に null を返す想定
      return response.data;
    } catch (error) {
      // 404 Not Found の場合はレポートが存在しないとみなし、 null を返す
      if (error.response && error.response.status === 404) {
        return null;
      }
      // その他のエラーはログに出力して再スロー
      console.error(
        `Capacity ID ${capacityId} によるレポート取得に失敗`,
        error.response ? error.response.data : error.message
      );
      throw error; // エラーを呼び出し元に伝える
    }
  });
};

export const getPreviousCohortHospitalReport = async (capacityId, cohortId) => {
  return retryRequest(async () => {
    try {
      const response = await api.get(
        `/hospital-reports/by-capacity/${capacityId}/previous-cohort?cohort_id=${cohortId}`,
      );
      return response.data;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        return null;
      }
      console.error(
        `Capacity ID ${capacityId} の前コホートレポート取得に失敗`,
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const getHospitalReportCopyCandidates = async (capacityId, cohortId) => {
  return retryRequest(async () => {
    try {
      const query = cohortId ? `?cohort_id=${cohortId}` : '';
      const response = await api.get(
        `/hospital-reports/by-capacity/${capacityId}/copy-candidates${query}`,
      );
      return response.data;
    } catch (error) {
      console.error(
        `Failed to fetch hospital report copy candidates for capacity ${capacityId}:`,
        error.response ? error.response.data : error.message,
      );
      throw error;
    }
  });
};

export const classifyHospitalReportFreeform = async (payload) => {
  return retryRequest(async () => {
    try {
      const response = await api.post('/hospital-reports/ai/classify-freeform', payload);
      return response.data;
    } catch (error) {
      console.error(
        'Failed to classify hospital report freeform text:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const getHospitalReportAiUsageStatus = async (params = {}) => {
  return retryRequest(async () => {
    try {
      const queryParams = new URLSearchParams();
      if (params.department_term_capacity_id) {
        queryParams.append('department_term_capacity_id', params.department_term_capacity_id);
      }
      if (params.term) {
        queryParams.append('term', params.term);
      }
      if (params.cohort_id) {
        queryParams.append('cohort_id', params.cohort_id);
      }
      const queryStr = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await api.get(`/hospital-reports/ai/usage-status${queryStr}`);
      return response.data;
    } catch (error) {
      console.error(
        'Failed to fetch hospital report AI usage status:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

export const refineHospitalReportDraft = async (payload) => {
  return retryRequest(async () => {
    try {
      const response = await api.post('/hospital-reports/ai/refine', payload);
      return response.data;
    } catch (error) {
      console.error(
        'Failed to refine hospital report draft:',
        error.response ? error.response.data : error.message
      );
      throw error;
    }
  });
};

// --- 試験関連API関数 ---

/**
 * 指定された試験回の全ブロックの問題データを取得する
 * @param {number} examYear - 試験回 (例: 115)
 * @returns {Promise<object>} - バックエンドからのレスポンスデータ (ExamQuestionsResponseスキーマに準拠)
 */
export const fetchAllBlockQuestionsForYear = async (examYear) => {
  return retryRequest(async () => {
    try {
      const response = await api.get(`/exam/${examYear}/questions_all_blocks`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching questions for year ${examYear}:`, error);
      const errData = error.response?.data;
      const customError = {
        message: errData?.detail ||
          (typeof errData === 'string' ? errData : null) ||
          error.message ||
          `問題データの取得に失敗しました (年度: ${examYear})`,
        status: error.response?.status,
        isAxiosError: error.isAxiosError,
        request: error.request,
        response: error.response
      };
      throw customError;
    }
  });
};

/**
 * ユーザーの回答を送信して採点結果を取得する
 * @param {number} examYear - 試験回
 * @param {Array<object>} submissions - ユーザーの回答リスト(UserSubmissionスキーマの配列)
 * @returns {Promise<object>} - 採点結果 (ScoreResultResponseスキーマに準拠)
 */
export const submitAllBlockAnswers = async (examYear, submissions) => {
  return retryRequest(async () => {
    try {
      // バックエンドの UserSubmissionsRequest スキーマに合わせてリクエストボディを作成
      // バックエンドのAPIパスは /exam/{exam_year}/score_all_blocks で、
      // リクエストボディが { exam_year: int, submissions: List[UserSubmission] } の場合
      const requestBody = {
        exam_year: examYear, // バックエンドのスキーマに exam_year があれば含める
        submissions: submissions,
      };
      // もしバックエンドの UserSubmissionsRequest スキーマが { submissions: List[UserSubmission] } のみで、
      // exam_year はパスパラメータからのみ取得する場合、requestBody は { submissions: submissions } となる。
      // 現在のバックエンドの UserSubmissionsRequest は exam_year を含んでいるので上記でOK。

      const response = await api.post(`/exam/${examYear}/score_all_blocks`, requestBody);
      return response.data;
    } catch (error) {
      console.error('Error submitting answers:', error);
      const errData = error.response?.data;
      const customError = {
        message: errData?.detail ||
          (typeof errData === 'string' ? errData : null) ||
          error.message ||
          '回答の送信と採点に失敗しました。',
        status: error.response?.status,
        isAxiosError: error.isAxiosError,
        request: error.request,
        response: error.response
      };
      throw customError;
    }
  }, 0); // 採点処理はリトライしない(0 retries)
};

export const fetchMyPastAttempts = async (skip = 0, limit = 100) => {
  return retryRequest(async () => {
    try {
      const response = await api.get(`/history/attempts?skip=${skip}&limit=${limit}`);
      return response.data; // PastExamAttemptSummaryResponseSchema[]
    } catch (error) {
      // ... (エラーハンドリング) ...
      console.error('Error fetching past attempts:', error);
      const errData = error.response?.data;
      const err = new Error(errData?.detail || '過去の採点履歴の取得に失敗しました。');
      err.status = error.response?.status;
      err.responseData = errData;
      throw err;
    }
  });
};

export const fetchAttemptDetails = async (attemptId) => {
  return retryRequest(async () => {
    try {
      const response = await api.get(`/history/attempts/${attemptId}`);
      return response.data; // ExamAttemptResponseSchema
    } catch (error) {
      // ... (エラーハンドリング) ...
      console.error(`Error fetching attempt details for ID ${attemptId}:`, error);
      const errData = error.response?.data;
      const err = new Error(errData?.detail || '採点結果詳細の取得に失敗しました。');
      err.status = error.response?.status;
      err.responseData = errData;
      throw err;
    }
  });
};

export const fetchQuestionHistory = async (questionNumber, limit = 3) => {
  return retryRequest(async () => {
    try {
      const response = await api.get(`/history/question_history/${questionNumber}?limit=${limit}`);
      return response.data; // QuestionPastResultResponseSchema[]
    } catch (error) {
      // ... (エラーハンドリング) ...
      console.error(`Error fetching history for question ${questionNumber}:`, error);
      const errData = error.response?.data;
      const err = new Error(errData?.detail || `問${questionNumber} の過去の解答履歴取得に失敗しました。`);
      err.status = error.response?.status;
      err.responseData = errData;
      throw err;
    }
  });
};

export const fetchAllIncorrectQuestions = async () => {
  return retryRequest(async () => {
    try {
      const response = await api.get('/analysis/all_incorrect_questions');
      return response.data; // AllIncorrectQuestionsResponse
    } catch (error) {
      console.error('Error fetching all incorrect questions:', error);
      const errData = error.response?.data;
      const err = new Error(errData?.detail || '間違えた問題一覧の取得に失敗しました。');
      err.status = error.response?.status;
      err.responseData = errData;
      throw err;
    }
  });
};


export const downloadAllHospitalReportsPdf = async () => {
  return retryRequest(async () => {
    try {
      const response = await api.get('/hospital-reports/pdf/all', {
        responseType: 'blob',
      });

      const disposition = response.headers?.['content-disposition'] || '';
      let filename = 'HospitalReports_All.pdf';
      const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)/i);
      if (match && match[1]) {
        try {
          filename = decodeURIComponent(match[1]);
        } catch {
          filename = match[1];
        }
      }

      return { blob: response.data, filename };
    } catch (error) {
      console.error('Error downloading combined hospital report PDF:', error);
      const errData = error.response?.data;
      const message =
        errData?.detail ||
        (typeof errData === 'string' ? errData : null) ||
        error.message ||
        '病院レポートPDFの生成に失敗しました。';
      const err = new Error(message);
      err.status = error.response?.status;
      err.isAxiosError = error.isAxiosError;
      err.request = error.request;
      err.response = error.response;
      throw err;
    }
  });
};
export const fetchAggregatePerformance = async () => {
  return retryRequest(async () => {
    try {
      const response = await api.get('/analysis/aggregate_performance'); // バックエンドのAPIパスを確認
      return response.data;
    } catch (error) {
      console.error('Error fetching aggregate performance:', error);
      const errData = error.response?.data;
      const err = new Error(errData?.detail || '総合パフォーマンスデータの取得に失敗しました。');
      err.status = error.response?.status;
      err.responseData = errData;
      throw err;
    }
  });
};

// Announcements API
export const getAnnouncements = async () => {
  return withCache('announcements', () => retryRequest(async () => {
    const response = await api.get('/announcements/');
    return response.data;
  }), 30 * 60 * 1000); // 30 minutes TTL
};

export const getAdminAnnouncements = async () => {
  return retryRequest(async () => {
    const response = await api.get('/announcements/admin');
    return response.data;
  });
};

export const createAnnouncement = async (payload) => {
  return retryRequest(async () => {
    const response = await api.post('/announcements/', payload);
    return response.data;
  });
};

export const updateAnnouncement = async (id, payload) => {
  return retryRequest(async () => {
    const response = await api.put(`/announcements/${id}`, payload);
    return response.data;
  });
};

export const deleteAnnouncement = async (id) => {
  return retryRequest(async () => {
    const response = await api.delete(`/announcements/${id}`);
    return response.data;
  });
};

// Schedule API
export const getSchedules = async () => {
  return withCache('schedules', () => retryRequest(async () => {
    const response = await api.get('/schedules/');
    return response.data;
  }), TTL_MASTER_DATA);
};

export const createSchedule = async (schedule) => {
  return retryRequest(async () => {
    const response = await api.post('/schedules/', schedule);
    return response.data;
  });
};

export const updateSchedule = async (id, schedule) => {
  return retryRequest(async () => {
    const response = await api.put(`/schedules/${id}`, schedule);
    return response.data;
  });
};

export const deleteSchedule = async (id) => {
  return retryRequest(async () => {
    const response = await api.delete(`/schedules/${id}`);
    return response.data;
  });
};

// Cohorts API
export const getCohorts = async () => {
  return withCache('cohorts', () => retryRequest(async () => {
    const response = await api.get('/cohorts/');
    return response.data;
  }), TTL_MASTER_DATA);
};

export const getFixedAssignments = async () => {
  return retryRequest(async () => {
    const response = await api.get('/admin/assignments/fixed');
    return response.data;
  });
};

export const createFixedAssignment = async (data) => {
  return retryRequest(async () => {
    const response = await api.post('/admin/assignments/fixed', data);
    return response.data;
  });
};

export const deleteFixedAssignment = async (id) => {
  return retryRequest(async () => {
    const response = await api.delete(`/admin/assignments/fixed/${id}`);
    return response.data;
  });
};
